// Shared primitives for the member-chat UI: avatars, tick marks, colour helpers,
// time formatting, message-body sniffing and the icon set. Everything here is
// presentational and dependency-free so the panes stay readable.

import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { addOutsidePress, removeOutsidePress } from "../outsidePress";

export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/**
 * One column for the thread and the composer, so a sent bubble ends exactly
 * where the send button does.
 *
 * The thread used to be max-w-3xl centred inside xl:px-20 while the composer ran
 * edge to edge. In a narrow panel nobody noticed; on the creator template, where
 * the chat is a full-width page, that stranded ~350px of empty canvas to the
 * right of every message the member sent. The cap only bites on very wide
 * screens, where a full-bleed thread would give 1700px-long lines.
 */
export const CHAT_COL = "mx-auto w-full max-w-[1400px]";

/* --------------------------------------------------------------- popover */

/**
 * A menu anchored to an element and PORTALLED to <body>.
 *
 * Every menu in this UI hangs off something inside a scroll container — a
 * message in the thread canvas, a row in the inbox list. Positioned normally
 * they are clipped the moment they need more room than the container has left,
 * which is what ate the reaction row and "Reply" off the message menu whenever
 * it opened near the top of the thread: all you saw was its bottom half sitting
 * against the conversation header. Portalling escapes the clip, and the
 * placement pass below keeps the whole panel inside the viewport — flipping
 * above the anchor only when there is genuinely more room up there.
 *
 * It measures itself before being shown (one hidden frame), so the flip uses
 * the real height rather than a guess.
 */
export function Popover({
  anchorRef,
  open,
  onClose,
  align = "start", // which edge of the anchor the panel lines up with
  width,
  gap = 8,
  scrollable = true,
  className = "",
  children,
}) {
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);
  // Held in a ref, not a dep: callers pass inline arrows, and re-running the
  // effect on every render would re-place → re-render → re-place forever.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    const M = 8; // keep this far clear of every viewport edge
    const place = () => {
      const a = anchorRef?.current;
      const el = popRef.current;
      if (!a || !el) return;
      const r = a.getBoundingClientRect();
      const h = el.offsetHeight;
      const w = el.offsetWidth;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const below = vh - r.bottom - gap - M;
      const above = r.top - gap - M;
      let top = r.bottom + gap;
      if (h > below && above > below) top = r.top - gap - h;
      top = Math.max(M, Math.min(top, vh - h - M));
      let left = align === "end" ? r.right - w : r.left;
      left = Math.max(M, Math.min(left, vw - w - M));
      setPos({ top, left, maxHeight: vh - M * 2 });
    };
    place();
    const inside = (t) => {
      const el = popRef.current;
      const a = anchorRef?.current;
      return !!(t instanceof Node && ((el && el.contains(t)) || (a && a.contains(t))));
    };
    // No click-blocking backdrop: an invisible full-screen catcher swallows the
    // first click anywhere else on the page, so dismissing a menu costs a click
    // before you can press what you were aiming at. Watch the document instead —
    // the click both closes this and reaches its real target. The anchor is
    // exempt so its own toggle isn't fighting us.
    const onDown = (e) => {
      if (!inside(e.target)) closeRef.current?.();
    };
    const onKey = (e) => {
      if (e.key === "Escape") closeRef.current?.();
    };
    // The anchor moves with its scroller, so close rather than chase it — but
    // NOT when the scroll happens inside the panel itself (the emoji picker's
    // own list and its category jumps live in there, and closing on those made
    // the picker vanish the moment you used it).
    const onScroll = (e) => {
      if (!inside(e.target)) closeRef.current?.();
    };
    addOutsidePress(onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    window.addEventListener("keydown", onKey);
    return () => {
      removeOutsidePress(onDown);
      document.removeEventListener("touchstart", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, align, gap, anchorRef]);

  if (!open) return null;
  return createPortal(
    <div
      ref={popRef}
      className={`chat-pop fixed z-[96] rounded-2xl border border-slate-100 bg-surface p-1.5 shadow-[0_24px_60px_-16px_rgba(15,23,42,0.45)] ${
        scrollable ? "thin-scroll overflow-y-auto" : ""
      } ${className}`}
      style={{
        top: pos ? pos.top : 0,
        left: pos ? pos.left : 0,
        width: width || "max-content",
        maxWidth: "calc(100vw - 16px)",
        maxHeight: scrollable ? pos?.maxHeight : undefined,
        // Hidden for the measuring frame only — never visible mispositioned.
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {children}
    </div>,
    document.body
  );
}

/* ---------------------------------------------------------------- colours */

/** Darken a #rrggbb hex by `amt` (0–1). Returns the input unchanged if unparsable. */
export function darken(hex, amt = 0.18) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const ch = (h) => Math.max(0, Math.round(parseInt(h, 16) * (1 - amt)));
  return `rgb(${ch(m[1])}, ${ch(m[2])}, ${ch(m[3])})`;
}

/** Lighten a #rrggbb hex toward white by `amt` (0–1). Passthrough if unparsable. */
export function lighten(hex, amt = 0.14) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return hex;
  const ch = (h) => {
    const v = parseInt(h, 16);
    return Math.min(255, Math.round(v + (255 - v) * amt));
  };
  return `rgb(${ch(m[1])}, ${ch(m[2])}, ${ch(m[3])})`;
}

/** The brand gradient used by the send button, unread pills and accents. */
export const brandGradient = (primary) => ({
  background: `linear-gradient(135deg, ${primary} 0%, ${darken(primary, 0.22)} 100%)`,
});

/**
 * Premium fill for the sender's own bubbles — a soft top sheen fading through
 * the brand colour into a deeper base, lifted by a brand-tinted shadow and a
 * hairline inner highlight so the bubble reads as a glossy, floating chip.
 */
export const sentBubble = (primary) => ({
  // A soft brand tint (WhatsApp-style) with dark text — clearly branded but light
  // enough to read against, not a heavy saturated block.
  backgroundImage: `linear-gradient(160deg, ${lighten(primary, 0.8)} 0%, ${lighten(primary, 0.7)} 100%)`,
  boxShadow: `0 2px 8px -3px ${primary}2e, 0 1px 2px rgba(16,24,40,0.06)`,
});

/* ------------------------------------------------------------------- time */

/** Inbox-row timestamp: time today, weekday this week, else a short date. */
export function shortTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

/** Clock time only — shown inside message bubbles. */
export function clockTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** Full date+time — used by the "message info" row. */
export function fullTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function lastSeenText(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "last seen just now";
  if (sec < 3600) return `last seen ${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `last seen ${Math.floor(sec / 3600)}h ago`;
  const days = Math.floor(sec / 86400);
  if (days < 7) return `last seen ${days}d ago`;
  return `last seen ${d.toLocaleDateString([], { month: "short", day: "numeric" })}`;
}

export function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diff = Math.round((new Date(now.toDateString()) - new Date(d.toDateString())) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString([], {
    month: "long",
    day: "numeric",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
}

/* ------------------------------------------------------- message payloads */
// Messages are stored as a single text body, so attachments are recognised from
// the URL they contain. Keep every sniffer here so the rules stay in one place.

const URL_RE = /^https?:\/\/\S+$/i;

export function isImageUrl(s) {
  const v = (s || "").trim();
  return URL_RE.test(v) && (/\.(gif|png|jpe?g|webp|avif)(\?\S*)?$/i.test(v) || /giphy\.com|tenor\.com/i.test(v));
}

export function isVideoUrl(s) {
  const v = (s || "").trim();
  // webm/mkv/ogv are containers used for VIDEO uploads — keep them here so a
  // video never falls through to the audio (voice-note) renderer.
  return URL_RE.test(v) && /\.(mp4|mov|m4v|webm|mkv|avi|ogv)(\?\S*)?$/i.test(v);
}

export function isAudioUrl(s) {
  const v = (s || "").trim();
  return URL_RE.test(v) && /\.(m4a|mp3|ogg|wav|aac|opus)(\?\S*)?$/i.test(v);
}

export function isFileUrl(s) {
  const v = (s || "").trim();
  return URL_RE.test(v) && /\.(pdf|docx?|xlsx?|pptx?|csv|zip|rar|txt)(\?\S*)?$/i.test(v);
}

/** A human filename for a file/audio attachment URL. */
export function fileNameFromUrl(s) {
  try {
    const path = new URL(s).pathname;
    return decodeURIComponent(path.split("/").pop() || "file");
  } catch {
    return "file";
  }
}

/** Display filename with our upload's "-<6hex>" uniqueness suffix stripped. */
export function prettyFileName(s) {
  return fileNameFromUrl(s).replace(/-[0-9a-f]{6}(\.[^.]+)$/i, "$1");
}

// File-type → label + accent colour for the document card icon.
const FILE_KINDS = {
  pdf: { label: "PDF", color: "#ef4444" },
  doc: { label: "DOC", color: "#2563eb" },
  docx: { label: "DOC", color: "#2563eb" },
  xls: { label: "XLS", color: "#16a34a" },
  xlsx: { label: "XLS", color: "#16a34a" },
  csv: { label: "CSV", color: "#16a34a" },
  ppt: { label: "PPT", color: "#f97316" },
  pptx: { label: "PPT", color: "#f97316" },
  txt: { label: "TXT", color: "rgb(var(--c-slate-500))" },
  zip: { label: "ZIP", color: "#8b5cf6" },
  rar: { label: "RAR", color: "#8b5cf6" },
};

/** { name, ext, label, color } for a document attachment URL. */
export function fileMeta(s) {
  const name = prettyFileName(s);
  const ext = (name.split(".").pop() || "").toLowerCase();
  const kind = FILE_KINDS[ext] || { label: (ext || "file").toUpperCase().slice(0, 4), color: "rgb(var(--c-slate-500))" };
  return { name, ext, ...kind };
}

/** One-word label for an inbox row preview ("Photo", "Voice message", …). */
/** Attachment kind ('image'|'video'|'audio'|'file') for a (possibly captioned)
 *  body, or null for plain text / links. Drives the preview icon. */
export function attachmentType(body) {
  const { url } = attachmentParts(body);
  if (!url) return null;
  if (isImageUrl(url)) return "image";
  if (isVideoUrl(url)) return "video";
  if (isAudioUrl(url)) return "audio";
  return "file";
}

export function previewLabel(body) {
  if (!body) return "";
  // Captioned attachment ("<url>\n<caption>") → the caption, else a type label.
  // No emoji here: the caller renders a proper SVG glyph beside this text.
  const { url, caption } = attachmentParts(body);
  if (url) {
    if (caption) return caption;
    if (isImageUrl(url)) return "Photo";
    if (isAudioUrl(url)) return "Voice message";
    if (isVideoUrl(url)) return "Video";
    return fileNameFromUrl(url);
  }
  return body;
}

// Reply previews come from the server prefixed with a type emoji ("📷 caption").
// Split that leading glyph off so the UI can swap it for a matching SVG icon.
const PREVIEW_GLYPH_KIND = { "📷": "image", "🎬": "video", "🎵": "audio", "📄": "file" };
export function splitPreviewGlyph(text) {
  const s = (text || "").replace(/^\s+/, "");
  const first = [...s][0];
  const kind = PREVIEW_GLYPH_KIND[first];
  if (kind) return { kind, text: s.slice(first.length).trim() };
  return { kind: null, text: s };
}

/** SVG glyph for an attachment kind — used in inbox rows & reply previews. */
export function PreviewIcon({ kind, className = "h-3.5 w-3.5 shrink-0" }) {
  if (kind === "image") return <PhotoIcon className={className} />;
  if (kind === "video") return <VideoIcon className={className} />;
  if (kind === "audio") return <MusicIcon className={className} />;
  if (kind === "file") return <DocIcon className={className} />;
  return null;
}

export function isAttachment(body) {
  return isImageUrl(body) || isAudioUrl(body) || isVideoUrl(body) || isFileUrl(body);
}

/**
 * Split a possibly-captioned attachment message. A photo/video/file sent WITH a
 * caption is stored as one message: the attachment URL on the first line, the
 * caption text below it ("<url>\n<caption>"). Returns { url, caption } — url is
 * null when the body isn't an attachment (plain text / link / bare URL).
 */
export function attachmentParts(body) {
  const v = body || "";
  const nl = v.indexOf("\n");
  const head = (nl === -1 ? v : v.slice(0, nl)).trim();
  if (!isAttachment(head)) return { url: null, caption: "" };
  return { url: head, caption: nl === -1 ? "" : v.slice(nl + 1).trim() };
}

// Emoji-only messages render big and bubble-less (WhatsApp / iMessage behaviour).
const EMOJI_ONLY_RE =
  /^(?:\p{Extended_Pictographic}|\p{Emoji_Component}|[\u{1F3FB}-\u{1F3FF}]|️|‍|\s)+$/u;

/** Number of emoji when the body is nothing but emoji (0 = not emoji-only). */
export function emojiOnlyCount(body) {
  const t = (body || "").trim();
  if (!t || t.length > 24) return 0;
  let ok = false;
  try {
    ok = EMOJI_ONLY_RE.test(t);
  } catch {
    return 0; // engine without Unicode property escapes
  }
  if (!ok) return 0;
  const glyphs = Array.from(t.replace(/[️‍\s]|[\u{1F3FB}-\u{1F3FF}]/gu, ""));
  return glyphs.length > 0 && glyphs.length <= 3 ? glyphs.length : 0;
}

const LINK_SPLIT_RE = /(https?:\/\/[^\s]+)/g;

/** Render a text body with clickable links. Returns an array of React nodes. */
export function linkify(body, linkClass) {
  const parts = String(body || "").split(LINK_SPLIT_RE);
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <a
        key={i}
        href={p}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={linkClass}
      >
        {p}
      </a>
    ) : (
      p
    )
  );
}

/** youtube.com / youtu.be → embeddable id, else null. */
export function youtubeId(body) {
  const m = String(body || "").match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
  );
  return m ? m[1] : null;
}

/* ---------------------------------------------------------------- avatars */

/**
 * Circular avatar. `ring` draws the white halo used on lists, `gradientRing`
 * draws the brand story-ring used by the favourites strip, and `online` adds
 * the presence dot.
 */
export function Avatar({ name, img, primary, size = 40, ring = false, gradientRing = false, online = false }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  const inner = (
    <span
      className={`flex h-full w-full items-center justify-center overflow-hidden rounded-full font-bold text-white ${
        ring ? "ring-2 ring-surface" : ""
      }`}
      style={{ backgroundColor: primary, fontSize: size * 0.4 }}
    >
      {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : initial}
    </span>
  );
  const dot = Math.max(8, Math.round(size * 0.26));
  return (
    // inline-flex (not plain inline) so width/height always apply, even when the
    // parent isn't a flex container — a bare <span> would ignore both.
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      {gradientRing ? (
        <span
          className="flex h-full w-full items-center justify-center rounded-full p-[2px]"
          style={brandGradient(primary)}
        >
          <span className="h-full w-full rounded-full bg-surface p-[2px]">{inner}</span>
        </span>
      ) : (
        inner
      )}
      {online && (
        <span
          className="absolute bottom-0 right-0 rounded-full border-2 border-surface bg-emerald-500"
          style={{ width: dot, height: dot }}
        />
      )}
    </span>
  );
}

/** WhatsApp-style ticks: ✓ sent, ✓✓ delivered, blue ✓✓ read. */
export function ReadTicks({ read, delivered, className = "h-3 w-[18px]", muted = false }) {
  const color = read ? "#38bdf8" : muted ? "rgba(255,255,255,0.7)" : "#94a3b8";
  if (!delivered && !read) {
    return (
      <svg viewBox="0 0 12 12" className={`${className} shrink-0`} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 6.5l3 3 6.2-7" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 18 12" className={`${className} shrink-0`} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 6.5l3 3 6.2-7" />
      <path d="M6.5 6.5l3 3 6.2-7" />
    </svg>
  );
}

/* ------------------------------------------------------------------ icons */
// One stroke-based set so weights stay consistent across every pane.

const Ico = ({ className = "h-4 w-4", d, fill = "none", width = "1.8", children, viewBox = "0 0 24 24" }) => (
  <svg className={className} fill={fill} viewBox={viewBox} stroke={fill === "none" ? "currentColor" : "none"} strokeWidth={width}>
    {children || <path strokeLinecap="round" strokeLinejoin="round" d={d} />}
  </svg>
);

export const StarIcon = ({ className, filled }) => (
  <svg className={className || "h-4 w-4"} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M12 3.5l2.6 5.27 5.82.85-4.21 4.1.99 5.79L12 16.77l-5.2 2.74.99-5.79-4.21-4.1 5.82-.85L12 3.5z" />
  </svg>
);
export const BellIcon = (p) => <Ico {...p} d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1" />;
export const BellOffIcon = (p) => <Ico {...p} d="M9 5.8A6 6 0 0118 11v3.2a2 2 0 00.6 1.4L20 17H8m-2.4 0H4l1.4-1.4A2 2 0 006 14.2V11M9 21a3 3 0 005.7-1M4 4l16 16" />;
export const BanIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M5.6 5.6l12.8 12.8" />
  </svg>
);
export const PinIcon = (p) => (
  <Ico {...p}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 17v5" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"
    />
  </Ico>
);
export const ArchiveIcon = (p) => <Ico {...p} d="M5 8h14M5 8a2 2 0 01-2-2V5a1 1 0 011-1h16a1 1 0 011 1v1a2 2 0 01-2 2M5 8v10a1 1 0 001 1h12a1 1 0 001-1V8M10 12h4" />;
export const TrashIcon = (p) => (
  <Ico {...p}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
  </Ico>
);
export const PhotoIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <rect x="4" y="5" width="16" height="14" rx="2" /><circle cx="9" cy="10" r="1.4" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 17l4-4 3 3 3-3 4 4" />
  </svg>
);
export const VideoIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="6" width="13" height="12" rx="2.5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 10l4.2-2.4a.6.6 0 011 .5v7.8a.6.6 0 01-1 .5L16 14" />
  </svg>
);
export const MusicIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 18V6l10-2v12" />
    <circle cx="6.5" cy="18" r="2.5" /><circle cx="16.5" cy="16" r="2.5" />
  </svg>
);
export const SearchIcon = (p) => <Ico {...p} d="M21 21l-4.3-4.3M11 19a8 8 0 100-16 8 8 0 000 16z" />;
export const PlusIcon = (p) => <Ico {...p} d="M12 5v14m7-7H5" />;
export const CloseIcon = (p) => <Ico {...p} d="M6 18L18 6M6 6l12 12" />;
export const ChevronLeft = (p) => <Ico {...p} d="M15 19l-7-7 7-7" />;
export const ChevronRight = (p) => <Ico {...p} d="M9 5l7 7-7 7" />;
export const ChevronDown = (p) => <Ico {...p} d="M6 9l6 6 6-6" />;
export const SendIcon = (p) => <Ico {...p} d="M6 12L3.5 4.5l17 7.5-17 7.5L6 12zm0 0h6" />;
export const MicIcon = (p) => <Ico {...p} d="M12 15a3 3 0 003-3V6a3 3 0 00-6 0v6a3 3 0 003 3zm7-3a7 7 0 01-14 0m7 7v3" />;
export const ReplyIcon = (p) => <Ico {...p} d="M9 17l-5-5 5-5M4 12h11a4 4 0 014 4v2" />;
export const ForwardIcon = (p) => <Ico {...p} d="M15 17l5-5-5-5M20 12H9a4 4 0 00-4 4v2" />;
export const CopyIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15V5a2 2 0 012-2h10" />
  </svg>
);
export const EditIcon = (p) => <Ico {...p} d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />;
export const InfoIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="9" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 11v5m0-8.2v.2" />
  </svg>
);
export const FlagIcon = (p) => <Ico {...p} d="M5 21V4m0 0h11l-1.5 3L16 10H5" />;
export const DownloadIcon = (p) => <Ico {...p} d="M12 4v11m0 0l-4-4m4 4l4-4M4 19h16" />;
export const DocIcon = (p) => <Ico {...p} d="M8 4h6l4 4v12a1 1 0 01-1 1H8a1 1 0 01-1-1V5a1 1 0 011-1zm6 0v5h5" />;
export const CheckIcon = (p) => <Ico {...p} d="M5 13l4 4L19 7" />;
export const SmileIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M9 14.5c.9.9 1.9 1.3 3 1.3s2.1-.4 3-1.3M9.2 9.5v.2M14.8 9.5v.2" />
  </svg>
);
export const UnreadDotIcon = ({ className = "h-3 w-3", style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5" /></svg>
);
export const DotsIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="12" cy="19" r="1.7" />
  </svg>
);
export const SparkIcon = (p) => <Ico {...p} d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />;
export const ChatGlyph = (p) => <Ico {...p} d="M8 10.5h8M8 14h5m-9 6.2l3.6-2.1H18a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v14.2z" />;
export const ShieldIcon = (p) => <Ico {...p} d="M12 3l7 3v5.5c0 4.3-2.9 8-7 9.5-4.1-1.5-7-5.2-7-9.5V6l7-3z" />;
export const PaletteIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3a9 9 0 100 18h1.5a2 2 0 001.6-3.2 2 2 0 011.6-3.2H19a2.5 2.5 0 002.5-2.5A9 9 0 0012 3z" />
    <circle cx="8" cy="10" r="1" fill="currentColor" stroke="none" /><circle cx="12" cy="7.5" r="1" fill="currentColor" stroke="none" /><circle cx="15.8" cy="10" r="1" fill="currentColor" stroke="none" />
  </svg>
);
